import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type { ProjectUtilisation } from '@/types/utilisation';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface ProjectAllocationSectionProps {
  projectUtilisation: ProjectUtilisation[];
  onProjectClick?: (project: ProjectUtilisation) => void;
}

export const ProjectAllocationSection: React.FC<ProjectAllocationSectionProps> = ({
  projectUtilisation,
  onProjectClick,
}) => {
  // Get top 5 projects by total hours
  const top5Projects = [...projectUtilisation]
    .sort((a, b) => b.totalHours - a.totalHours)
    .slice(0, 5);

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle>Project Allocation</CardTitle>
          <CardDescription>
            Top 5 projects by total hours
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px]">Rank</TableHead>
                <TableHead>Project Name</TableHead>
                <TableHead className="text-right">Total Hours</TableHead>
                <TableHead className="text-right">Utilization %</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {top5Projects.length > 0 ? (
                top5Projects.map((project, index) => (
                  <TableRow 
                    key={project.projectId}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => onProjectClick?.(project)}
                  >
                    <TableCell className="font-medium">
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-semibold">
                        {index + 1}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{project.projectName} - {project.projectCode}</span>
                          {project.isBillable && (
                            <Badge variant="default" className="text-xs">
                              Billable
                            </Badge>
                          )}
                        </div>
                        <span className="text-sm text-muted-foreground">
                          {project.clientName}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex flex-col items-end gap-1">
                        <span className="font-semibold text-lg">
                          {project.totalHours.toFixed(1)}h
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {project.employeeCount} {project.employeeCount === 1 ? 'employee' : 'employees'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex flex-col items-end gap-2">
                        <span className="font-semibold text-lg">
                          {(project.utilisationRate * 100).toFixed(1)}%
                        </span>
                        <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary transition-all"
                            style={{ width: `${project.utilisationRate * 100}%` }}
                          />
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                    No project data available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
      </CardContent>
    </Card>
  );
};
